'use client';

import { useEffect } from 'react';

import type { Basket, CustomerData, Setter, StorageUserSession } from '@libs/common-next';
import { useLocalStorage } from '@libs/common-next';
import type { Nullable } from '@libs/core';
import { VELOCITY_LOCAL_STORAGE_KEY } from '@libs/core';

type UseSessionLocalStorageParams = {
    customer?: Nullable<CustomerData>;
    basket?: Basket;
};

export const useUserSessionLocalStorage = ({
    customer,
    basket,
}: UseSessionLocalStorageParams): [StorageUserSession | undefined, Setter<StorageUserSession>] => {
    const [userSession, setUserSession] = useLocalStorage<StorageUserSession>(
        VELOCITY_LOCAL_STORAGE_KEY,
        {},
        {
            syncData: false,
        }
    );

    // Sync localStorage with customer data (customer data is fetched once)
    useEffect(() => {
        // We care to update the state only if we have data, because we display guest state by default
        if (customer?.first_name || customer?.auth_type || basket?.product_quantity_total) {
            setUserSession(prevState => ({
                ...prevState,
                firstName: customer?.first_name,
                isRegistered: customer?.auth_type === 'registered',
                basketCount: basket?.product_quantity_total,
            }));
        }
    }, [customer?.first_name, customer?.auth_type, basket?.product_quantity_total]);

    return [userSession, setUserSession];
};
